import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Chip, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  selectLatestResult,
  selectResultError,
  selectResultLoading,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import EmptyState from '../shared/EmptyState';
import MetricCard from '../shared/MetricCard';
import {
  classifyEvents,
  describeAction,
  describeOutcome,
  explainException,
  formatDurationMs,
  friendlyLocator,
  summaryForEvent,
} from '../../../utilities/locatorDrift/locatorFormatters';
import { resultsMetricColors, resultsTabStyles } from './resultsStyles';

export default function ResultsTab() {
  const data = useSelector(selectLatestResult);
  const loading = useSelector(selectResultLoading);
  const error = useSelector(selectResultError);
  const [expandedIndex, setExpandedIndex] = useState(null);

  if (loading) {
    return <CircularProgress sx={resultsTabStyles.loader} />;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!data) {
    return <EmptyState>No locator result loaded yet.</EmptyState>;
  }

  const events = Array.isArray(data.events) ? data.events : [];
  const classifications = classifyEvents(events);
  const visibleEvents = events
    .map((event, index) => ({
      event,
      index,
      classification: classifications[index],
    }))
    .filter((item) => item.classification !== 'skipped');
  const passCount = visibleEvents.filter((item) => item.classification === 'success').length;
  const failCount = visibleEvents.filter((item) => item.classification === 'fail').length;

  return (
    <Stack spacing={2}>
      <Box sx={resultsTabStyles.metaRow}>
        <Typography variant="body2">
          <strong>Scenario:</strong> {data.scenario || 'Unknown'}
        </Typography>
        <Typography variant="body2">
          <strong>Platform:</strong> {data.platform || 'Unknown'}
        </Typography>
        <Typography variant="body2">
          <strong>App:</strong> {data.app_version || 'Unknown'}
        </Typography>
        <Typography variant="body2">
          <strong>Collected:</strong> {data.collected_at || 'Unknown'}
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <MetricCard label="Total Steps" value={visibleEvents.length} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard label="Passed" value={passCount} color={resultsMetricColors.passed} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard label="Failed" value={failCount} color={resultsMetricColors.failed} />
        </Grid>
      </Grid>
      {visibleEvents.length === 0 ? (
        <EmptyState>No locator events recorded for this run.</EmptyState>
      ) : (
        <Paper variant="outlined" sx={resultsTabStyles.listPaper}>
          {visibleEvents.map(({ event, index, classification }, displayIndex) => {
            const isOpen = expandedIndex === index;
            const isFail = classification === 'fail';
            const action = describeAction(event.method);
            const target = friendlyLocator(event.locator_value);
            return (
              <Box key={`${index}-${event.locator_value}`}>
                <Box
                  onClick={() => setExpandedIndex(isOpen ? null : index)}
                  sx={resultsTabStyles.stepRow(isFail)}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={resultsTabStyles.stepNumber}
                  >
                    {displayIndex + 1}
                  </Typography>
                  {isFail ? (
                    <ErrorIcon color="error" fontSize="small" />
                  ) : (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={resultsTabStyles.stepDescription}>
                    <strong>{action}</strong> {target}
                  </Typography>
                  <Chip
                    size="small"
                    color={isFail ? 'error' : 'success'}
                    label={describeOutcome(event, false)}
                  />
                </Box>
                {isOpen && (
                  <Box sx={resultsTabStyles.detailPanel}>
                    <Typography variant="body2" fontWeight={800} sx={resultsTabStyles.detailTitle}>
                      {summaryForEvent(event, false)}
                    </Typography>
                    <Alert
                      severity={isFail ? 'error' : 'success'}
                      sx={resultsTabStyles.detailAlert}
                    >
                      {explainException(
                        event.exception_type,
                        event.method,
                        event.locator_by,
                        event.locator_value,
                        event.duration_ms,
                      )}
                    </Alert>
                    <Grid container spacing={1}>
                      {[
                        ['Method', event.method],
                        ['Locator type', event.locator_by],
                        ['Locator value', event.locator_value],
                        ['Duration', formatDurationMs(event.duration_ms)],
                        ['Exception', event.exception_type || 'None'],
                        ['Timestamp', event.timestamp || 'N/A'],
                      ].map(([label, value]) => (
                        <Grid item xs={12} md={6} key={label}>
                          <Typography variant="caption" color="text.secondary">
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={resultsTabStyles.detailValue}>
                            {value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}
    </Stack>
  );
}
